import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, CreditCard, Lock, Truck, Gift, PartyPopper, CheckCircle, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Payment Form Component
const PaymentForm = ({ orderId, amount, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const { error: submitError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/checkout/success`,
        },
        redirect: 'if_required',
      });

      if (submitError) {
        setError(submitError.message);
        toast.error(submitError.message);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Payment successful - confirm with backend
        try {
          await axios.post(`${API}/orders/${orderId}/confirm-payment`);
          toast.success('Payment successful!');
          onSuccess();
        } catch (confirmError) {
          console.error('Failed to confirm payment:', confirmError);
          // Payment still went through, just redirect
          onSuccess();
        }
      }
    } catch (err) {
      setError('An unexpected error occurred.');
      toast.error('Payment failed. Please try again.');
    }

    setProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement 
        options={{
          layout: 'tabs',
        }}
      />
      
      {error && (
        <div className="text-red-500 text-sm bg-red-50 p-3 rounded-lg">
          {error}
        </div>
      )}
      
      <Button 
        type="submit" 
        className="w-full btn-accent" 
        size="lg" 
        disabled={!stripe || processing}
        data-testid="pay-now-btn"
      >
        {processing ? (
          <span className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Processing Payment...
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Pay ${amount.toFixed(2)}
          </span>
        )}
      </Button>

      <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
        <Lock className="h-4 w-4" />
        <span>Secure payment powered by Stripe</span>
      </div>
    </form>
  );
};

const Checkout = () => {
  const { cart, fetchCart, clearCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [shippingSettings, setShippingSettings] = useState({
    shipping_rate: 3.99,
    free_shipping_threshold: 50.00,
    shipping_enabled: true
  });
  const [formData, setFormData] = useState({
    buyer_name: '',
    phone: '',
    shipping_address: '',
    shipping_city: '',
    shipping_state: '',
    shipping_zip: '',
    shipping_country: '',
  });
  
  // First-time buyer discount
  const [isFirstTimeBuyer, setIsFirstTimeBuyer] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const hasCheckedFirstTime = useRef(false);

  useEffect(() => {
    fetchShippingSettings();
    
    const hasPurchased = localStorage.getItem('brookzone_purchased') === 'true';
    if (!hasPurchased) {
      setIsFirstTimeBuyer(true);
    }
  }, []);

  useEffect(() => {
    if (cart.total > 0 && isFirstTimeBuyer && !hasCheckedFirstTime.current) {
      hasCheckedFirstTime.current = true;
      const discount = cart.total * 0.10;
      setDiscountAmount(discount);
      toast.success('🎉 First-time buyer discount applied: 10% off!');
    }
  }, [cart.total, isFirstTimeBuyer]);

  const fetchShippingSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setShippingSettings({
        shipping_rate: response.data.shipping_rate || 3.99,
        free_shipping_threshold: response.data.free_shipping_threshold || 50.00,
        shipping_enabled: response.data.shipping_enabled !== false
      });
    } catch (error) {
      console.error('Failed to fetch shipping settings:', error);
    }
  };

  // Detect if order is digital-only (all e-books, no physical products)
  const isDigitalOnly = cart.items.length > 0 && cart.items.every(item => item.is_ebook || item.is_digital);
  const hasPhysicalItems = cart.items.some(item => !item.is_ebook && !item.is_digital);

  const getShippingCost = () => {
    if (isDigitalOnly) return 0;
    if (!shippingSettings.shipping_enabled) return 0;
    const subtotalAfterDiscount = cart.total - discountAmount;
    if (subtotalAfterDiscount >= shippingSettings.free_shipping_threshold) return 0;
    return shippingSettings.shipping_rate;
  };

  const shippingCost = getShippingCost();
  const orderTotal = cart.total - discountAmount + shippingCost;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    
    // Only require shipping details for physical items
    if (hasPhysicalItems) {
      if (!formData.buyer_name || !formData.shipping_address || !formData.shipping_city || !formData.shipping_state || !formData.shipping_zip || !formData.shipping_country) {
        toast.error('Please fill in all required shipping details');
        return;
      }
    } else {
      if (!formData.buyer_name) {
        toast.error('Please enter your name');
        return;
      }
    }

    if (!cart.items || cart.items.length === 0) {
      toast.error('Your cart is empty');
      navigate('/cart');
      return;
    }

    setLoading(true);
    try {
      // Create order
      const orderData = {
        ...formData,
        discount_code: isFirstTimeBuyer ? 'FIRST_TIME_BUYER' : null,
        discount_amount: discountAmount
      };
      
      const orderResponse = await axios.post(`${API}/orders/create`, orderData);
      const order = orderResponse.data;
      setOrderId(order.id);

      // Create payment intent
      const checkoutResponse = await axios.post(`${API}/orders/${order.id}/checkout`, {
        origin_url: window.location.origin,
      });

      setClientSecret(checkoutResponse.data.client_secret);
      
      // Mark as purchased
      localStorage.setItem('brookzone_purchased', 'true');
      
    } catch (error) {
      console.error('Failed to create order:', error);
      toast.error(error.response?.data?.detail || 'Failed to create order. Please try again.');
    }
    setLoading(false);
  };

  const handlePaymentSuccess = () => {
    setPaymentSuccess(true);
    clearCart(); // Clear cart in frontend
    setTimeout(() => {
      navigate('/checkout/success?payment=complete');
    }, 2000);
  };

  if (!cart.items || cart.items.length === 0) {
    navigate('/cart');
    return null;
  }

  // Show success message
  if (paymentSuccess) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="h-10 w-10 text-green-600" />
        </div>
        <h1 className="text-2xl font-bold text-green-800 mb-2">Payment Successful!</h1>
        <p className="text-gray-600 mb-4">Thank you for your order. Redirecting...</p>
        <Loader2 className="h-6 w-6 animate-spin mx-auto text-green-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="checkout-page">
      {/* First-time buyer discount banner */}
      {isFirstTimeBuyer && discountAmount > 0 && (
        <div className="mb-6 bg-gradient-to-r from-green-100 to-emerald-100 border border-green-300 rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="bg-green-200 p-3 rounded-full">
              <PartyPopper className="h-6 w-6 text-green-700" />
            </div>
            <div>
              <p className="font-semibold text-green-800 text-lg">🎉 First-Time Buyer Discount Applied!</p>
              <p className="text-sm text-green-600">
                You&apos;re saving <span className="font-bold">${discountAmount.toFixed(2)}</span> (10% off) on your first order!
              </p>
            </div>
          </div>
        </div>
      )}

      <h1 className="font-serif text-3xl font-bold text-foreground mb-8">Checkout</h1>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Shipping & Payment Form */}
        <div className="space-y-6">
          {/* Shipping/Order Form */}
          {!clientSecret && (
            <Card>
              <CardHeader>
                <CardTitle className="font-serif flex items-center gap-2">
                  {isDigitalOnly ? (
                    <>
                      <Download className="h-5 w-5" />
                      Order Information
                    </>
                  ) : (
                    <>
                      <Truck className="h-5 w-5" />
                      Shipping Information
                    </>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateOrder} className="space-y-4">
                  {/* Digital-only notice */}
                  {isDigitalOnly && (
                    <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 mb-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Download className="h-4 w-4 text-primary" />
                        <span className="font-medium text-primary">Digital order — no shipping needed</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Your e-books will be available for download immediately after payment.</p>
                    </div>
                  )}

                  {/* Buyer Name - Always required */}
                  <div className="space-y-2">
                    <Label htmlFor="buyer_name">Full Name *</Label>
                    <Input
                      id="buyer_name"
                      name="buyer_name"
                      placeholder="John Doe"
                      value={formData.buyer_name}
                      onChange={handleInputChange}
                      disabled={loading}
                      required
                      data-testid="name-input"
                    />
                  </div>
                  
                  {/* Phone - Optional */}
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number (optional)</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={formData.phone}
                      onChange={handleInputChange}
                      disabled={loading}
                      data-testid="phone-input"
                    />
                  </div>
                  
                  {/* Shipping fields - only for physical items */}
                  {hasPhysicalItems && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="shipping_address">Street Address *</Label>
                        <Input
                          id="shipping_address"
                          name="shipping_address"
                          placeholder="123 Main Street"
                          value={formData.shipping_address}
                          onChange={handleInputChange}
                          disabled={loading}
                          data-testid="address-input"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="shipping_city">City *</Label>
                          <Input
                            id="shipping_city"
                            name="shipping_city"
                            placeholder="New York"
                            value={formData.shipping_city}
                            onChange={handleInputChange}
                            disabled={loading}
                            data-testid="city-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="shipping_state">State *</Label>
                          <Input
                            id="shipping_state"
                            name="shipping_state"
                            placeholder="NY"
                            value={formData.shipping_state}
                            onChange={handleInputChange}
                            disabled={loading}
                            data-testid="state-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="shipping_zip">ZIP Code *</Label>
                          <Input
                            id="shipping_zip"
                            name="shipping_zip"
                            placeholder="10001"
                            value={formData.shipping_zip}
                            onChange={handleInputChange}
                            disabled={loading}
                            data-testid="zip-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="shipping_country">Country</Label>
                        <Input
                          id="shipping_country"
                          name="shipping_country"
                          placeholder="United States"
                          value={formData.shipping_country}
                          onChange={handleInputChange}
                          disabled={loading}
                          data-testid="country-input"
                        />
                      </div>
                    </>
                  )}

                  <div className="pt-4">
                    <Button type="submit" className="w-full btn-accent" size="lg" disabled={loading} data-testid="continue-to-payment-btn">
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Creating Order...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <CreditCard className="h-5 w-5" />
                          Continue to Payment
                        </span>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Payment Form - Shows after shipping is filled */}
          {clientSecret && (
            <Card>
              <CardHeader>
                <CardTitle className="font-serif flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Payment Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                {hasPhysicalItems && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm">
                    <p className="text-gray-600">
                      <strong>Shipping to:</strong> {formData.shipping_address}, {formData.shipping_city}, {formData.shipping_state} {formData.shipping_zip}, {formData.shipping_country}
                    </p>
                  </div>
                )}
                {isDigitalOnly && (
                  <div className="mb-4 p-3 bg-primary/5 rounded-lg text-sm flex items-center gap-2">
                    <Download className="h-4 w-4 text-primary" />
                    <span className="text-primary font-medium">Digital download — available after payment</span>
                  </div>
                )}
                
                <Elements 
                  stripe={stripePromise} 
                  options={{ 
                    clientSecret,
                    appearance: {
                      theme: 'stripe',
                      variables: {
                        colorPrimary: '#8B5A2B',
                        borderRadius: '8px',
                      },
                    },
                  }}
                >
                  <PaymentForm 
                    orderId={orderId} 
                    amount={orderTotal}
                    onSuccess={handlePaymentSuccess}
                  />
                </Elements>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Order Summary */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="font-serif">Order Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {cart.items.map((item) => (
                <div key={item.product_id} className="flex gap-3 py-2 border-b last:border-0">
                  <img
                    src={item.image_url || 'https://images.pexels.com/photos/256450/pexels-photo-256450.jpeg'}
                    alt={item.name}
                    className="w-16 h-16 object-cover rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm line-clamp-1">{item.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {item.is_ebook || item.is_digital ? (
                        <span className="text-primary">Digital Download</span>
                      ) : (
                        `Qty: ${item.quantity}`
                      )}
                    </p>
                  </div>
                  <p className="price-tag font-medium">${item.subtotal.toFixed(2)}</p>
                </div>
              ))}

              <div className="pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>${cart.total.toFixed(2)}</span>
                </div>
                
                {isFirstTimeBuyer && discountAmount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span className="flex items-center gap-1">
                      <Gift className="h-3 w-3" />
                      First-Time Buyer Discount (10%)
                    </span>
                    <span>-${discountAmount.toFixed(2)}</span>
                  </div>
                )}
                
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Shipping</span>
                  {isDigitalOnly ? (
                    <span className="text-green-600 font-medium">N/A (Digital)</span>
                  ) : shippingCost === 0 ? (
                    <span className="text-green-600 font-medium">Free</span>
                  ) : (
                    <span>${shippingCost.toFixed(2)}</span>
                  )}
                </div>
                {!isDigitalOnly && shippingCost === 0 && (
                  <div className="flex items-center gap-2 text-green-600 text-xs">
                    <Truck className="h-3 w-3" />
                    <span>Free shipping applied!</span>
                  </div>
                )}
                {isDigitalOnly && (
                  <div className="flex items-center gap-2 text-primary text-xs">
                    <Download className="h-3 w-3" />
                    <span>Instant digital download</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg pt-2 border-t">
                  <span>Total</span>
                  <span className="price-tag">${orderTotal.toFixed(2)}</span>
                </div>
              </div>

              {/* Payment Methods Info */}
              <div className="pt-4 border-t">
                <p className="text-xs text-muted-foreground text-center mb-3">We accept</p>
                <div className="flex justify-center gap-2 flex-wrap">
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">💳 Cards</span>
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">🍎 Apple Pay</span>
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">📱 Google Pay</span>
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">💰 Klarna</span>
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">💵 Afterpay</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
